import type { ReactNode, JSX} from "react";
import { cn } from "./cn";

export type TextVariant = "heading" | "subheading" | "title" | "body" | "caption" | "tiny" | "overline" | "micro";
export type TextColor = "default" | "secondary" | "tertiary" | "accent" | "white" | "inherit";
export type TextWeight = "normal" | "medium" | "semibold" | "bold";

export interface TextProps {
  /** Predefined size/style variant */
  readonly variant?: TextVariant;
  /** Font weight override */
  readonly weight?: TextWeight;
  /** Text color */
  readonly color?: TextColor;
  /** Truncate with ellipsis */
  readonly truncate?: boolean;
  /** Line-through style */
  readonly strikethrough?: boolean;
  /** Prevent shrinking */
  readonly noShrink?: boolean;
  /** Text alignment */
  readonly align?: "left" | "center" | "right";
  /** Line height */
  readonly leading?: "tight" | "normal" | "relaxed";
  /** Render as element type */
  readonly as?: "span" | "div" | "p";
  /** Extra className (margins only) */
  readonly className?: string;
  readonly children: ReactNode;
}

const variantStyles: Readonly<Record<TextVariant, string>> = {
  heading: "text-lg font-semibold text-content",
  subheading: "text-base font-semibold text-content",
  title: "text-sm font-semibold text-content",
  body: "text-sm text-content",
  caption: "text-xs text-content-tertiary",
  tiny: "text-[10px] text-content-tertiary",
  overline: "text-xs font-semibold text-content-tertiary uppercase tracking-wide",
  micro: "text-[9px] text-content-tertiary",
};

const colorStyles: Readonly<Record<TextColor, string>> = {
  default: "text-content",
  secondary: "text-content-secondary",
  tertiary: "text-content-tertiary",
  accent: "text-accent",
  white: "text-white",
  inherit: "",
};

const weightStyles: Readonly<Record<TextWeight, string>> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

const alignStyles: Readonly<Record<string, string>> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const leadingStyles: Readonly<Record<string, string>> = {
  tight: "leading-tight",
  normal: "leading-normal",
  relaxed: "leading-relaxed",
};

export function Text({
  variant = "body",
  weight,
  color,
  truncate,
  strikethrough,
  noShrink,
  align,
  leading,
  as: Tag = "div",
  className,
  children,
}: TextProps): JSX.Element {
  return (
    <Tag
      className={cn(
        variantStyles[variant],
        weight && weightStyles[weight],
        color && colorStyles[color],
        truncate && "truncate",
        strikethrough && "line-through",
        noShrink && "shrink-0",
        align && alignStyles[align],
        leading && leadingStyles[leading],
        className,
      )}
    >
      {children}
    </Tag>
  );
}
